import React, { useMemo } from 'react'
import {
  DocumentRequest,
  REQUEST_USE_CASE,
} from '@trustero/trustero-api-web/lib/request/request_pb'
import { useDocumentRequests } from 'src/components/async/DocumentRequest'
import { useControlsbyIds } from 'src/components/async/model/control/useControls'
import { usePolicies } from 'src/components/async/policy/usePolicies'
import { useEvidenceIndexPage } from 'src/pages/Evidence/evidence.hooks'
import { useRisks } from 'src/pages/Risks/risks.hooks'
import { useVendors } from 'src/pages/Vendors/vendors.hooks'
import { useDRLRequest } from 'src/pages/Requests/requests.hooks'
import {
  EvidenceIdFilter,
  GetEvidenceIndexPageRequest,
} from '@trustero/trustero-api-web/lib/attachment/attachment_pb'
import {
  REQUEST_USE_CASE_TO_LABEL,
  REQUEST_OBJECT_TYPE_LABEL,
  REQUEST_OBJECT_IDS,
  USE_CASE_SLUG,
} from 'src/pages/Requests/requests.constants'
import { FilterName, FilterParam } from '../FilterBar.types'
import { DropdownItemType, NestedFilterType } from './FilterDropdown.constants'
import { FilterDropdown } from './FilterDropdown'
import { LoadingFilterDropdown } from './FilterDropdown.components'

const OBJECT_TYPE_KEYS = [
  'controls',
  'evidence',
  'policy',
  'vendor',
  'risk',
] as const
type ObjectTypeKey = (typeof OBJECT_TYPE_KEYS)[number]

const OBJECT_TYPE_TO_FILTER_PARAM: Record<ObjectTypeKey, FilterParam> = {
  controls: FilterParam.REQUEST_CONTROLS,
  evidence: FilterParam.REQUEST_EVIDENCE,
  policy: FilterParam.REQUEST_POLICY,
  vendor: FilterParam.REQUEST_VENDOR,
  risk: FilterParam.REQUEST_RISK,
}

export const RequestTypeFilterDropdown = React.memo(
  ({ isNested = false }: { isNested?: boolean }): JSX.Element | null => {
    const drlRequest = useDRLRequest()
    const { data: requestsData, isLoading: requestsLoading } =
      useDocumentRequests({ request: drlRequest })

    const requests = useMemo(
      () => requestsData?.getItemsList() ?? [],
      [requestsData],
    )

    // Derive which object IDs are linked to each request type
    const linkedIdsByTypeAndObjType = useMemo(() => {
      const map: Record<string, Record<ObjectTypeKey, Set<string>>> = {}
      requests.forEach((r: DocumentRequest) => {
        const slug = USE_CASE_SLUG[r.getUseCaseType()]
        if (!slug) return
        if (!map[slug]) {
          map[slug] = {
            controls: new Set(),
            evidence: new Set(),
            policy: new Set(),
            vendor: new Set(),
            risk: new Set(),
          }
        }
        OBJECT_TYPE_KEYS.forEach((key) => {
          REQUEST_OBJECT_IDS[key](r).forEach((id) => map[slug][key].add(id))
        })
      })
      return map
    }, [requests])

    const allControlIds = useMemo(
      () =>
        Array.from(
          new Set(
            requests.flatMap((r: DocumentRequest) => r.getControlIdsList()),
          ),
        ),
      [requests],
    )

    const allVendorIds = useMemo(
      () =>
        Array.from(
          new Set(
            requests.flatMap((r: DocumentRequest) => r.getVendorIdsList()),
          ),
        ),
      [requests],
    )

    const allPolicyIds = useMemo(
      () =>
        Array.from(
          new Set(
            requests.flatMap((r: DocumentRequest) => r.getPolicyModelIdsList()),
          ),
        ),
      [requests],
    )

    const allRiskIds = useMemo(
      () =>
        Array.from(
          new Set(requests.flatMap((r: DocumentRequest) => r.getRiskIdsList())),
        ),
      [requests],
    )

    const allEvidenceIds = useMemo(
      () =>
        Array.from(
          new Set(
            requests.flatMap((r: DocumentRequest) => r.getEvidenceIdsList()),
          ),
        ),
      [requests],
    )

    const evidenceRequest = useMemo(() => {
      const req = new GetEvidenceIndexPageRequest()
      // The filter's own options are derived from this; windowed, options for out-of-range
      // evidence would quietly vanish from the dropdown.
      req.setIncludeOutOfWindow(true)
      if (allEvidenceIds.length > 0) {
        req.setEvidenceIdFilter(
          new EvidenceIdFilter().setEvidenceIdsList(allEvidenceIds),
        )
      }
      return req
    }, [allEvidenceIds])

    const { data: controlsData, isLoading: controlsLoading } =
      useControlsbyIds(allControlIds)
    const { data: vendorsData, isLoading: vendorsLoading } = useVendors(
      undefined,
      allVendorIds.length > 0,
    )
    const { data: policiesData, isLoading: policiesLoading } = usePolicies(
      undefined,
      allPolicyIds.length > 0,
    )
    const { data: risksData, isLoading: risksLoading } = useRisks(
      undefined,
      allRiskIds.length > 0,
    )
    const { data: evidenceData, isLoading: evidenceLoading } =
      useEvidenceIndexPage(evidenceRequest, allEvidenceIds.length > 0)

    const isLoading =
      requestsLoading ||
      controlsLoading ||
      vendorsLoading ||
      policiesLoading ||
      risksLoading ||
      evidenceLoading

    if (isLoading) {
      return (
        <LoadingFilterDropdown
          filterName={FilterName.REQUEST_USE_CASE_TYPE}
          isNested={isNested}
        />
      )
    }

    // Build name lookup maps
    const controlNameById: Record<string, string> = {}
    controlsData?.getItemsList().forEach((c) => {
      const label = `${c.getModelId()} ${c.getName()}`.trim()
      controlNameById[c.getId()] = label
      controlNameById[c.getModelId()] = label
    })

    const vendorNameById: Record<string, string> = {}
    vendorsData?.getVendorsList().forEach((v) => {
      vendorNameById[v.getId()] = v.getName()
    })

    const policyNameById: Record<string, string> = {}
    policiesData?.getItemsList().forEach((p) => {
      const name = p.getName()
      policyNameById[p.getModelId()] = name
      policyNameById[p.getId()] = name
    })

    const riskNameById: Record<string, string> = {}
    risksData?.getRisksList().forEach((r) => {
      riskNameById[r.getId()] = `${r.getCustomId()} ${r.getName()}`.trim()
    })

    // EvidenceWrapper.getEvidenceId() is the raw evidences.id UUID — same as what
    // DocumentRequest.getEvidenceIdsList() stores, so it matches for filtering.
    const evidenceNameById: Record<string, string> = {}
    evidenceData?.getItemsList().forEach((e) => {
      const caption = e.getCaption()
      if (caption) evidenceNameById[e.getEvidenceId()] = caption
    })

    const nameMapByObjType: Record<ObjectTypeKey, Record<string, string>> = {
      controls: controlNameById,
      evidence: evidenceNameById,
      policy: policyNameById,
      vendor: vendorNameById,
      risk: riskNameById,
    }

    // Build level-3 nested filter values per (slug, objType)
    const buildLevel3 = (
      slug: string,
      objType: ObjectTypeKey,
    ): NestedFilterType => {
      const ids = linkedIdsByTypeAndObjType[slug]?.[objType] ?? new Set()
      const nameMap = nameMapByObjType[objType]
      const items: DropdownItemType[] = Array.from(ids)
        .map((id) => ({ value: id, label: nameMap[id] || id }))
        .sort((a, b) =>
          typeof a.label === 'string' && typeof b.label === 'string'
            ? a.label.localeCompare(b.label)
            : 0,
        )
      return {
        filterParam: OBJECT_TYPE_TO_FILTER_PARAM[objType],
        filterValues: items,
        hasSearch: true,
      }
    }

    // Build level-2 nested filter values per slug
    const buildLevel2 = (slug: string): NestedFilterType => {
      const objTypes = linkedIdsByTypeAndObjType[slug]
      const items: DropdownItemType[] = objTypes
        ? (OBJECT_TYPE_KEYS.filter((key) => objTypes[key].size > 0)
            .map((key) => {
              const level3 = buildLevel3(slug, key)
              // Only show object type if we resolved at least one named item
              if (level3.filterValues.length === 0) return null
              return {
                value: key,
                label: REQUEST_OBJECT_TYPE_LABEL[key],
                nestedFilter: level3,
              }
            })
            .filter(Boolean) as DropdownItemType[])
        : []
      return {
        filterParam: slug,
        filterValues: items,
      }
    }

    // Build level-1 filter values
    const filterValues: DropdownItemType[] = [
      ...(
        [
          REQUEST_USE_CASE.REQUEST_FOR_EVIDENCE,
          REQUEST_USE_CASE.REQUEST_FOR_INFORMATION,
          REQUEST_USE_CASE.REQUEST_FOR_ACTION,
          REQUEST_USE_CASE.REQUEST_FOR_VENDOR_ATTESTATION,
          REQUEST_USE_CASE.REQUEST_FOR_POLICY_DOCUMENT,
        ] as number[]
      ).map((useCase) => {
        const slug = USE_CASE_SLUG[useCase]
        return {
          value: slug,
          label: REQUEST_USE_CASE_TO_LABEL[useCase],
          nestedFilter: buildLevel2(slug),
        }
      }),
    ]

    return (
      <FilterDropdown
        filterParam={FilterParam.REQUEST_USE_CASE_TYPE}
        filterName={FilterName.REQUEST_USE_CASE_TYPE}
        filterValues={filterValues}
        hasNested
        parentMeansAll
        isNested={isNested}
      />
    )
  },
)

RequestTypeFilterDropdown.displayName = 'RequestTypeFilterDropdown'
